'use client';

import React, { forwardRef, useCallback, useRef, useState } from 'react';
import {
    Collection as AriaCollection,
    Header as AriaHeader,
    ListBox as AriaListBox,
    ListBoxSection as AriaListBoxSection,
    Virtualizer as AriaVirtualizer,
    type Key,
    type ListBoxProps as AriaListBoxProps,
} from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { SectionedGridLayout, type SectionedGridLayoutOptions } from './SectionedGridLayout';

const SEMANTIC_NAME_VIRTUAL_GRID = 'RomaAssetPickerVirtualGrid';
const SEMANTIC_NAME_SECTION = 'RomaAssetPickerSection';

// Solid for the first half, so the previous section's last row (and its selection ring) is fully hidden.
// Stops short of the scrollbar so it doesn't fade too.
const FADE_CLASS_NAME = 'pointer-events-none absolute right-3 left-0 h-5 from-bg-primary from-50% to-transparent';

export type AssetPickerVirtualGridProps<T extends object> = Omit<
    AriaListBoxProps<T>,
    'layout' | 'className' | 'disabledKeys' | 'disabledBehavior'
> & {
    /** Positions the tiles, from `useSectionedGrid`. */
    layout: SectionedGridLayout;
    /** Additional CSS classes for the grid. */
    className?: string;
};

export type AssetPickerSectionProps<T extends object> = {
    /** Section key, used to jump to the section. */
    id: Key;
    /** Section heading. */
    title: React.ReactNode;
    /** Tiles in the section, rendered by `children`. */
    items?: Iterable<T>;
    /** Tiles, or a function that renders a tile for each item. */
    children: React.ReactNode | ((item: T) => React.ReactElement);
    /** Additional CSS classes for the section. */
    className?: string;
};

/**
 * Creates the layout for `AssetPicker.VirtualGrid`, plus a way to jump to a section.
 * Options are read once, on first render.
 */
export const useSectionedGrid = (options: SectionedGridLayoutOptions) => {
    const [layout] = useState(() => new SectionedGridLayout(options));
    const gridRef = useRef<HTMLDivElement>(null);

    const scrollToSection = useCallback(
        (sectionKey: Key) => {
            const sectionScrollTop = layout.getSectionScrollTop(sectionKey);
            if (sectionScrollTop === undefined || !gridRef.current) return;

            gridRef.current.scrollTop = sectionScrollTop;
        },
        [layout],
    );

    return { layout, gridRef, scrollToSection };
};

/**
 * A grid that renders only the visible tiles and scrolls itself, for long sectioned lists like emoji.
 * Edges fade so the previous section doesn't peek in above a heading after a jump.
 */
const AssetPickerVirtualGridRoot = <T extends object>(
    { layout, className, ...props }: AssetPickerVirtualGridProps<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
) => (
    <div className="relative h-full">
        <AriaVirtualizer layout={layout}>
            <AriaListBox
                {...props}
                ref={ref}
                layout="grid"
                className={cx(SEMANTIC_NAME_VIRTUAL_GRID, 'h-full overflow-y-auto outline-hidden', className)}
            />
        </AriaVirtualizer>
        <div aria-hidden className={cx(FADE_CLASS_NAME, 'top-0 bg-linear-to-b')} />
        <div aria-hidden className={cx(FADE_CLASS_NAME, 'bottom-0 bg-linear-to-t')} />
    </div>
);

const AssetPickerVirtualGridWithRef = forwardRef(AssetPickerVirtualGridRoot);
AssetPickerVirtualGridWithRef.displayName = SEMANTIC_NAME_VIRTUAL_GRID;

export const AssetPickerVirtualGrid = AssetPickerVirtualGridWithRef as <T extends object>(
    props: AssetPickerVirtualGridProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;

/**
 * Groups tiles under a heading in a virtual grid, e.g. an emoji category.
 */
export const AssetPickerSection = <T extends object>({
    id,
    title,
    items,
    children,
    className,
}: AssetPickerSectionProps<T>) => (
    <AriaListBoxSection id={id} className={cx(SEMANTIC_NAME_SECTION, className)}>
        <AriaHeader className="pb-md text-md font-semibold tracking-tight text-primary">{title}</AriaHeader>
        <AriaCollection items={items}>{children}</AriaCollection>
    </AriaListBoxSection>
);

AssetPickerSection.displayName = SEMANTIC_NAME_SECTION;
